<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import { useHeader } from './useHeader';

import { useChatStore } from '@/stores/v2/ChatStore.ts';

import ScoutTrigger from '@/features/assistant/components/ScoutTrigger.vue';
import OPageTitle from '@/components/organisms/OPageTitle.vue';
import {
  notificationsOutlined,
  notificationsUnreadOutlined,
} from '@/assets/icons/materialSymbols/MaterialSymbols.ts';
import { headerIds } from '@/utils/DataTestIds.ts';
import AFrameworkIcon from '@/components/atoms/AFrameworkIcon.vue';

const emit = defineEmits(['toggleDrawer']);

const showDeviceBanner = ref(true);
const { scoutAvailable } = storeToRefs(useChatStore());

const {
  t,
  route,
  authStore,
  showUnread,
  unreadCount,
  showBackArrow,
  goBack,
  pageTitle,
  pageUniqueCode,
  frameworkData,
} = useHeader();

function drawerToggle() {
  emit('toggleDrawer');
}
</script>

<template>
  <q-header v-if="route.name !== 'not-found'" bordered class="row items-center">
    <!-- eslint-disable-next-line vue/no-restricted-class -- phones are not finished -->
    <div v-if="showDeviceBanner" class="row col-12 device-banner items-center lt-sm text-body2">
      <div class="row col-11 justify-center">
        <p>
          {{ t('The full Modulos Platform features are enabled for bigger resolution screens') }}.
        </p>
      </div>
      <q-space />
      <q-icon name="o_close" size="22px" class="text-muted" @click="showDeviceBanner = false" />
    </div>
    <q-toolbar class="no-wrap">
      <!-- From a tablet up the sidebar stays on screen, with its own toggle. -->
      <q-btn
        v-if="$q.screen.lt.sm"
        flat
        round
        dense
        icon="o_menu"
        class="text-muted q-mr-sm"
        :aria-label="t('Open menu')"
        @click="drawerToggle"
      />
      <h5
        v-if="
          (authStore.user && route.name === 'dashboard') ||
          (authStore.user && route.name === 'home')
        "
        :data-test-id="headerIds.greetingsText"
        class="greetings text-bold text-ellipsis"
      >
        {{ t('Hello, ') + authStore.user.firstname + ' ' + authStore.user.lastname + '!' }}
      </h5>

      <div v-else class="header-title row no-wrap items-center">
        <button
          v-if="showBackArrow"
          type="button"
          class="header-icon-action q-mr-sm text-muted"
          :aria-label="t('Back')"
          @click="goBack"
        >
          <q-icon name="o_arrow_back" size="24px" />
        </button>
        <AFrameworkIcon
          v-if="frameworkData"
          :framework="frameworkData"
          :dense="true"
          class="q-mr-sm"
        />
        <h3 v-if="pageUniqueCode" class="concept-code q-mr-sm text-muted">{{ pageUniqueCode }}</h3>
        <OPageTitle v-if="pageTitle" :title="pageTitle" />
      </div>

      <q-space />
      <!-- eslint-disable-next-line vue/no-restricted-class -- on a phone it shows in the drawer -->
      <div class="org-name__container gt-xs row items-center justify-center">
        <span class="text-body2 text-muted">{{
          authStore.user && 'organization' in authStore.user
            ? authStore.user.organization.name
            : null
        }}</span>
      </div>
      <ScoutTrigger v-if="scoutAvailable" :data-test-id="headerIds.scoutButton" class="q-mr-md" />
      <div class="notifications__container row items-center">
        <router-link
          to="/notifications"
          class="header-icon-action bell relative-position"
          :aria-label="t('Notifications')"
        >
          <q-icon
            v-if="!showUnread"
            :name="notificationsOutlined"
            :data-test-id="headerIds.notificationsIcon"
            size="24px"
            class="text-muted"
          />
          <q-icon
            v-if="showUnread"
            :name="notificationsUnreadOutlined"
            :data-test-id="headerIds.notificationsUnreadIcon"
            color="red-5"
            size="24px"
          />
          <span v-if="unreadCount" class="notification-badge bg-red-5 text-white">
            {{ unreadCount > 99 ? '99+' : unreadCount }}
          </span>
        </router-link>
      </div>
    </q-toolbar>
  </q-header>
</template>

<style lang="scss" scoped>
// A real button or link that looks like a bare icon.
.header-icon-action {
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  cursor: pointer;
}

.q-header {
  background: $white;
  border: none;
  box-shadow: 0px 0px 2px 2px rgba(0, 0, 0, 0.1);
  @include respond-to-min(sm) {
    height: $header-height;
  }

  .device-banner {
    color: rgba(0, 0, 0, 0.85);
    border-radius: 4px;
    border: 1px solid #ffe58f;
    background: #fffbe6;
    padding: 16px 30px;
  }

  :deep(svg) {
    cursor: pointer;
  }
  .q-toolbar {
    padding: 0 var(--page-gutter-x);

    // The title truncates; the actions keep their size.
    .header-title {
      flex: 0 1 auto;
      min-width: 0;
    }

    .greetings {
      min-width: 0;
    }
    // Short and identifying: the title truncates first.
    .concept-code {
      flex-shrink: 0;
      overflow: hidden;
      text-wrap: nowrap;
      text-overflow: ellipsis;
    }

    .org-name__container {
      height: 100%;

      @include respond-to-min(sm) {
        padding: 0 15px;
        margin-right: 10px;
        justify-content: flex-end;
      }
    }

    .language__containre {
      border-left: 1px solid $secondary-200;
      border-right: 1px solid $secondary-200;
    }

    .notifications__container {
      height: 100%;
      position: relative;

      .dot-icon {
        position: absolute;
        top: -4px;
        right: 0;
      }

      .bell {
        display: inline-flex;
        align-items: center;
      }

      .notification-badge {
        position: absolute;
        top: -5px;
        left: 60%;
        min-width: 17px;
        height: 17px;
        padding: 0 4px;
        border-radius: 9px;
        font-size: 10px;
        font-weight: 700;
        line-height: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        border: 1.5px solid $white;
        pointer-events: none;
      }
    }
  }
}
</style>
